import React, { useState, useEffect } from 'react';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useIsEditor } from '../hooks/useIsEditor';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  LinearProgress
} from '@mui/material';
import { Warning, CheckCircle, Info, Edit } from '@mui/icons-material';

const TagsManagement = ({ 
  open, 
  onClose, 
  accounts,  // Array of account objects
  onUpdate, 
  authenticatedFetch, 
  API_ENDPOINT 
}) => {
  const isAdmin = useIsAdmin();
  const isEditor = useIsEditor();
  const canEdit = isAdmin || isEditor;
  const [functionalUnit, setFunctionalUnit] = useState('');
  const [businessUnit, setBusinessUnit] = useState('');
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validation, setValidation] = useState(null);
  const [updateResults, setUpdateResults] = useState(null);
  const [showPreview, setShowPreview] = useState(true);
  
  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      // Pre-fill values if single account
      if (accounts.length === 1) {
        setFunctionalUnit(accounts[0].tags?.functionalUnit || '');
        setBusinessUnit(accounts[0].tags?.businessUnit || '');
      } else {
        setFunctionalUnit('');
        setBusinessUnit('');
      }
      setValidation(null);
      setUpdateResults(null);
      setShowPreview(true);
    }
  }, [open, accounts]);
  
  const validateTags = async () => {
    if (!functionalUnit && !businessUnit) {
      setValidation(null);
      return;
    }
    
    setValidating(true);
    try {
      const response = await authenticatedFetch(
        `${API_ENDPOINT}/accounts/tags/validate`,
        {
          method: 'POST',
          body: JSON.stringify({
            functionalUnit,
            businessUnit
          })
        }
      );
      const result = await response.json();
      setValidation(result);
    } catch (err) {
      console.error('Validation error:', err);
      setValidation({
        valid: false,
        errors: [`Validation failed: ${err.message}`],
        warnings: []
      });
    } finally {
      setValidating(false);
    }
  };
  
  const handleUpdate = async () => {
    setLoading(true);
    setUpdateResults(null);
    setShowPreview(false);
    
    try {
      const accountIds = accounts.map(acc => acc.id);
      const response = await authenticatedFetch(
        `${API_ENDPOINT}/accounts/bulk/tags`,
        {
          method: 'PUT',
          body: JSON.stringify({
            account_ids: accountIds,
            tags: {
              functionalUnit,
              businessUnit
            }
          })
        }
      );
      const result = await response.json();
      setUpdateResults(result);
      
      if (result.success) {
        onUpdate();
      }
    } catch (err) {
      console.error('Update error:', err);
      setUpdateResults({
        success: false,
        error: err.message,
        total: accounts.length,
        succeeded: 0,
        failed: accounts.length
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Trigger validation when values change
  useEffect(() => {
    if (functionalUnit || businessUnit) {
      const timer = setTimeout(() => validateTags(), 500);
      return () => clearTimeout(timer);
    } else {
      setValidation(null);
    }
  }, [functionalUnit, businessUnit]);
  
  const canUpdate = !loading && !validating && validation?.valid && (functionalUnit || businessUnit);
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: '0.95rem', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {accounts.length === 1 ? <Edit sx={{ fontSize: 18, color: 'primary.main' }} /> : <Warning color="warning" sx={{ fontSize: 18 }} />}
          <Typography variant="body1" fontWeight={600}>
            {accounts.length === 1 
              ? `Edit Tags — ${accounts[0].name}`
              : `Bulk Update Tags — ${accounts.length} Accounts`
            }
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 2 }}>
        {/* Warning Alert */}
        {accounts.length > 1 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.85rem' }}>
              You are about to update tags for {accounts.length} accounts.
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
              This action will update the following AWS Organizations tags:
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {functionalUnit && <li><code>VA-BusinessUnitAlignment</code> → {functionalUnit}</li>}
              {businessUnit && <li><code>VA-BusinessUnit</code> → {businessUnit}</li>}
            </Box>
          </Alert>
        )}
        
        {accounts.length === 1 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
              Editing tags for account: <strong>{accounts[0].name}</strong> ({accounts[0].id})
            </Typography>
          </Alert>
        )}
        
        {/* Show affected accounts preview (only for bulk) */}
        {showPreview && accounts.length > 1 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={600} fontSize="0.8rem" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info fontSize="small" />
              Affected Accounts:
            </Typography>
            
            {accounts.length <= 10 ? (
              <List dense sx={{ maxHeight: 200, overflow: 'auto', bgcolor: 'grey.50', borderRadius: 1 }}>
                {accounts.map(account => (
                  <ListItem key={account.id}>
                    <ListItemText 
                      primary={account.name}
                      secondary={`${account.id} • ${account.ouName || 'No OU'}`}
                      primaryTypographyProps={{ variant: 'body2', sx: { fontSize: '0.85rem' } }}
                      secondaryTypographyProps={{ variant: 'caption', sx: { fontSize: '0.75rem' } }}
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.85rem' }}>
                  First 10 of {accounts.length} accounts:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                  {accounts.slice(0, 10).map(account => (
                    <Chip 
                      key={account.id}
                      label={account.name}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                  {accounts.length > 10 && (
                    <Chip 
                      label={`+${accounts.length - 10} more`}
                      size="small"
                      color="primary"
                    />
                  )}
                </Box>
              </Box>
            )}
          </Box>
        )}
        
        <Divider sx={{ my: 2 }} />
        
        {/* Tag input fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Functional Unit"
            value={functionalUnit}
            onChange={(e) => setFunctionalUnit(e.target.value)}
            fullWidth
            size="small"
            disabled={loading}
            helperText="Maps to VA-BusinessUnitAlignment tag"
            placeholder="e.g., EES, UW, Claims, CDO"
            sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' }, '& .MuiFormHelperText-root': { fontSize: '0.75rem' } }}
          />
          
          <TextField
            label="Business Unit"
            value={businessUnit}
            onChange={(e) => setBusinessUnit(e.target.value)}
            fullWidth
            size="small"
            disabled={loading}
            helperText="Maps to VA-BusinessUnit tag"
            placeholder="e.g., Insurance, Energy, Financial Services"
            sx={{ '& .MuiInputBase-input': { fontSize: '0.85rem' }, '& .MuiFormHelperText-root': { fontSize: '0.75rem' } }}
          />
        </Box>
        
        {/* Validation feedback */}
        {validating && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} />
            <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Validating tag values...</Typography>
          </Box>
        )}
        
        {validation && !validating && (
          <Box sx={{ mt: 2 }}>
            {validation.errors && validation.errors.length > 0 && (
              <Alert severity="error" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.85rem' }}>
                  Validation Errors:
                </Typography>
                {validation.errors.map((error, idx) => (
                  <Typography key={idx} variant="body2" sx={{ fontSize: '0.85rem' }}>• {error}</Typography>
                ))}
              </Alert>
            )}
            
            {validation.warnings && validation.warnings.length > 0 && (
              <Alert severity="warning" icon={<Warning />}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5, fontSize: '0.85rem' }}>
                  Warnings:
                </Typography>
                {validation.warnings.map((warning, idx) => (
                  <Typography key={idx} variant="body2" sx={{ fontSize: '0.85rem' }}>
                    • {warning}
                  </Typography>
                ))}
              </Alert>
            )}
            
            {validation.valid && validation.warnings.length === 0 && (
              <Alert severity="success" icon={<CheckCircle />}>
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>Tag values are valid and ready to apply</Typography>
              </Alert>
            )}
          </Box>
        )}
        
        {/* Update progress and results */}
        {loading && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom sx={{ fontSize: '0.85rem' }}>
              Updating {accounts.length} accounts...
            </Typography>
            <LinearProgress />
          </Box>
        )}
        
        {updateResults && (
          <Box sx={{ mt: 2 }}>
            {updateResults.success ? (
              <Alert severity="success" icon={<CheckCircle />}>
                <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>
                  Successfully updated {updateResults.succeeded} of {updateResults.total} accounts!
                </Typography>
              </Alert>
            ) : (
              <Alert severity="error">
                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '0.85rem' }}>
                  Update completed with errors
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                  • Succeeded: {updateResults.succeeded}
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                  • Failed: {updateResults.failed}
                </Typography>
                {updateResults.error && (
                  <Typography variant="body2" sx={{ mt: 1, fontSize: '0.85rem' }}>
                    Error: {updateResults.error}
                  </Typography>
                )}
                {updateResults.results && updateResults.results.filter(r => !r.success).length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                      Failed accounts:
                    </Typography>
                    {updateResults.results.filter(r => !r.success).slice(0, 5).map((result, idx) => (
                      <Typography key={idx} variant="caption" display="block" sx={{ fontSize: '0.75rem' }}>
                        • {result.account_id}: {result.error}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {updateResults?.success ? 'Close' : 'Cancel'}
        </Button>
        {canEdit && (
          <Button 
            onClick={handleUpdate}
            variant="contained"
            color="warning"
            disabled={!canUpdate}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Updating...
              </>
            ) : (
              `Update ${accounts.length} Accounts`
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TagsManagement;
